/**
 * Commercial RE Lending Page
 * Comprehensive commercial real estate lending programs
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FixFlipCalculator from "../components/FixFlipCalculator";
import DSCRCalculator from "../components/DSCRCalculator";
import { 
  ArrowRight, 
  Building2, 
  Home, 
  Hammer, 
  TrendingUp,
  CheckCircle,
  DollarSign,
  Clock,
  Percent,
  Shield,
  Users,
  ChevronDown,
  ChevronUp,
  Calculator
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80";

const loanPrograms = [
  {
    id: "fix-flip",
    title: "Fix & Flip Loans",
    icon: Hammer,
    description: "Short-term financing for property rehab projects with up to 100% of purchase price + 100% renovation costs.",
    color: "#e8821c",
    highlights: [
      "Up to 100% of Purchase Price",
      "Up to 100% of Renovation Costs",
      "12-18 Month Terms",
      "Interest Only on Outstanding Balance"
    ],
    details: {
      collateral: "Non-Owner Occupied 1-4 Family Real Estate; Condos; Townhomes; 5+ Unit Apartments; Mixed-Use Properties",
      ltv: "Up to 100% of Purchase Price + 100% of Renovation Cost; Not to Exceed 75% of ARV",
      term: "12 – 18 Months",
      loanAmount: "$75k – $2M (1-4 Family) | $250k – $3M (Multi-Family 5+)",
      minFico: "650",
      rates: "Starting at 9.49%",
      minValue: "$100k (Single-Family) | $75k (2-4 Family) | $375k (Multi-Family 5+)"
    },
    tiers: [
      { level: "New Investor (0-2 Flips)", ltv: "Up to 80% Purchase + 100% Rehab", maxArv: "70% ARV", rate: "Starting at 10.74%" },
      { level: "Intermediate (3-5 Flips)", ltv: "Up to 90% Purchase + 100% Rehab", maxArv: "72.5% ARV", rate: "Starting at 10.24%" },
      { level: "Experienced (10+ Flips)", ltv: "Up to 95% Purchase + 100% Rehab", maxArv: "75% ARV", rate: "Starting at 9.24%" }
    ]
  },
  {
    id: "long-term-rental",
    title: "Long-Term Rental Loans",
    icon: Home,
    description: "30-year financing for rental property investors with competitive rates and flexible terms.",
    color: "#48d480",
    highlights: [
      "30-Year Fixed Options",
      "Rates Starting at 5.25%",
      "Up to 80% LTV",
      "DSCR-Based Qualification"
    ],
    details: {
      collateral: "Non-Owner Occupied 1-4 Family Real Estate; Condos; Townhomes; PUD",
      ltv: "Purchase: Up to 80% | Refinance: Up to 80% | Cash-Out: Up to 75%",
      term: "30-Year Fixed, Hybrid ARMs (5/1, 7/1, 10/1) & Interest-Only Options",
      loanAmount: "$70k – $1.5M",
      minFico: "660",
      rates: "Starting at 5.25%",
      minValue: "$115k As-Is Appraised Value"
    },
    tiers: [
      { level: "740+ FICO", ltv: "Up to 80% LTV", maxArv: "80% LTC", rate: "Best Rates" },
      { level: "700-739 FICO", ltv: "Up to 80% LTV", maxArv: "80% LTC", rate: "Competitive Rates" },
      { level: "680-699 FICO", ltv: "Up to 75% LTV", maxArv: "75% LTC", rate: "Standard Rates" },
      { level: "660-679 FICO", ltv: "Up to 65% LTV", maxArv: "65% LTC", rate: "Qualifying Rates" }
    ]
  },
  {
    id: "multifamily",
    title: "Multi-Family Loans",
    icon: Building2,
    description: "Short-term bridge and long-term financing for 5+ unit apartment buildings and mixed-use properties.",
    color: "#0f2d52",
    highlights: [
      "5+ Unit Properties",
      "Bridge & Long-Term Options",
      "Mixed-Use Eligible",
      "Up to $2M Loan Amount"
    ],
    details: {
      collateral: "Multi-Family Apartment Buildings (5+ Units); Mixed-Use Properties (70%+ Residential)",
      ltv: "Bridge: Up to 75% | Fix & Flip: Up to 80% + 100% Rehab",
      term: "Short-Term: 12-18 Months | Long-Term: 30 Years",
      loanAmount: "Short-Term: $250k – $2M | Long-Term: $150k – $1.5M",
      minFico: "Short-Term: 650 | Long-Term: 700",
      rates: "Competitive market rates",
      minValue: "Varies by program"
    },
    tiers: [
      { level: "Stabilized Bridge - Purchase", ltv: "Up to 75% As-Is Value", maxArv: "N/A", rate: "Bridge Rates" },
      { level: "Stabilized Bridge - Refinance", ltv: "Up to 70% As-Is Value", maxArv: "N/A", rate: "Bridge Rates" },
      { level: "Fix & Flip - Experienced", ltv: "Up to 80% + 100% Rehab", maxArv: "70% ARV", rate: "Flip Rates" },
      { level: "Long-Term (700+ FICO)", ltv: "Up to 70% As-Is Value", maxArv: "70% LTC", rate: "Long-Term Rates" }
    ]
  },
  {
    id: "new-construction",
    title: "New Construction Loans",
    icon: TrendingUp,
    description: "Ground-up construction financing with competitive rates and flexible draw schedules.",
    color: "#3b82f6",
    highlights: [
      "Ground-Up Construction",
      "12-24 Month Terms",
      "Up to 90% LTC",
      "Rates Starting at 9.84%"
    ],
    details: {
      collateral: "Non-Owner Occupied Single-Family Properties; Condos; Townhomes",
      ltv: "Up to 75% As-Is Value | Up to 75% ARV | Up to 90% LTC",
      term: "12 – 24 Months",
      loanAmount: "$100k – $2M",
      minFico: "650",
      rates: "Starting at 9.84%",
      minValue: "$175k As-Completed Value"
    },
    tiers: [
      { level: "Tier 1 (10+ Projects)", ltv: "Up to 75% As-Is + 80% LTC", maxArv: "75% ARV, 90% LTC", rate: "Best Rates" },
      { level: "Experienced (5+ Projects)", ltv: "Up to 75% As-Is + 80% LTC", maxArv: "75% ARV, 90% LTC", rate: "Competitive Rates" },
      { level: "Intermediate (1-4 Projects)", ltv: "Up to 75% As-Is + 75% LTC", maxArv: "70% ARV, 85% LTC", rate: "Standard Rates" },
      { level: "New (0 Projects)", ltv: "Up to 70% As-Is + 70% LTC", maxArv: "65% ARV, 85% LTC", rate: "Qualifying Rates" }
    ]
  },
  {
    id: "rental-portfolio",
    title: "Rental Portfolio Loans",
    icon: Users,
    description: "Large-scale financing for experienced and institutional investors with 20+ units.",
    color: "#8b5cf6",
    highlights: [
      "$2M – $75M+ Loan Amounts",
      "Non-Recourse Options",
      "Multi-State Portfolios",
      "5, 7, & 10 Year Terms"
    ],
    details: {
      collateral: "Non-Owner Occupied 1-4 Family; Condos; Townhomes; Multi-Family (5-9 Units)",
      ltv: "Up to 70% LTV (Interest-Only Options Available)",
      term: "5, 7, & 10 Year Terms",
      loanAmount: "$2M – $75M+",
      minFico: "680+",
      rates: "Competitive institutional rates",
      minValue: "Individual: $100k (1-4 Units), $125k (5-9 Units) | Avg Portfolio: $100k"
    },
    tiers: [
      { level: "720+ FICO - Purchase", ltv: "Up to 70% LTV", maxArv: "70% LTC", rate: "Best Rates" },
      { level: "700-719 FICO - Purchase", ltv: "Up to 65% LTV", maxArv: "65% LTC", rate: "Competitive Rates" },
      { level: "680-699 FICO - Purchase", ltv: "Up to 60% LTV", maxArv: "60% LTC", rate: "Standard Rates" }
    ]
  }
];

const benefits = [
  { icon: Clock, title: "Fast Closings", description: "Close in as little as 10-14 days with streamlined processing" },
  { icon: DollarSign, title: "Competitive Rates", description: "Industry-leading rates starting as low as 5.25%" },
  { icon: Percent, title: "High Leverage", description: "Up to 100% of purchase price + 100% of renovation costs" },
  { icon: Shield, title: "Non-Recourse Options", description: "Protect your personal assets with non-recourse financing" }
];

function LoanProgramCard({ program, isExpanded, onToggle }) {
  const Icon = program.icon;
  
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl">
      {/* Header */}
      <div 
        className="p-6 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${program.color}20` }}
            >
              <Icon size={28} style={{ color: program.color }} />
            </div>
            <div>
              <h3 
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-xl font-bold text-[#0f2d52] mb-2"
              >
                {program.title}
              </h3>
              <p 
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-gray-600 text-sm"
              >
                {program.description}
              </p>
            </div>
          </div>
          <button className="text-gray-400 hover:text-[#0f2d52] transition-colors p-2">
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </button>
        </div>
        
        {/* Highlights */}
        <div className="flex flex-wrap gap-2 mt-4">
          {program.highlights.map((highlight, idx) => (
            <span 
              key={idx}
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full"
              style={{ backgroundColor: `${program.color}15`, color: program.color }}
            >
              <CheckCircle size={12} />
              {highlight}
            </span>
          ))}
        </div>
      </div>
      
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-6">
          {/* Loan Criteria */}
          <div className="mb-6">
            <h4 
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-lg font-bold text-[#0f2d52] mb-4"
            >
              Loan Criteria
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <p className="text-xs text-gray-500 uppercase mb-1">Collateral</p>
                <p className="text-sm text-[#0f2d52] font-medium">{program.details.collateral}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <p className="text-xs text-gray-500 uppercase mb-1">LTV</p>
                <p className="text-sm text-[#0f2d52] font-medium">{program.details.ltv}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <p className="text-xs text-gray-500 uppercase mb-1">Term</p>
                <p className="text-sm text-[#0f2d52] font-medium">{program.details.term}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <p className="text-xs text-gray-500 uppercase mb-1">Loan Amount</p>
                <p className="text-sm text-[#0f2d52] font-medium">{program.details.loanAmount}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <p className="text-xs text-gray-500 uppercase mb-1">Minimum FICO</p>
                <p className="text-sm text-[#0f2d52] font-medium">{program.details.minFico}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <p className="text-xs text-gray-500 uppercase mb-1">Rates</p>
                <p className="text-sm font-medium" style={{ color: program.color }}>{program.details.rates}</p>
              </div>
            </div>
          </div>
          
          {/* Leverage Tiers */}
          <div>
            <h4 
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-lg font-bold text-[#0f2d52] mb-4"
            >
              Typical Leverages & Rates
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Experience Level</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Max LTV</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Max ARV/LTC</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-medium">Rates</th>
                  </tr>
                </thead>
                <tbody>
                  {program.tiers.map((tier, idx) => (
                    <tr key={idx} className="border-b border-gray-100 last:border-0">
                      <td className="py-3 px-4 font-medium text-[#0f2d52]">{tier.level}</td>
                      <td className="py-3 px-4 text-gray-600">{tier.ltv}</td>
                      <td className="py-3 px-4 text-gray-600">{tier.maxArv}</td>
                      <td className="py-3 px-4 font-medium" style={{ color: program.color }}>{tier.rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* CTA */}
          <div className="mt-6 flex justify-center">
            <a 
              href="https://app.trulli.ai/marketplace/legacytriangle"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white font-bold px-8 py-3 rounded-lg transition-colors"
              style={{ backgroundColor: program.color }}
            >
              Apply for {program.title} <ArrowRight size={18} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CommercialRELending() {
  const [expandedProgram, setExpandedProgram] = useState(null);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleProgram = (programId) => {
    setExpandedProgram(expandedProgram === programId ? null : programId);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="commercial-re-lending-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center bg-[#0f2d52] overflow-hidden pt-32">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMG} 
            alt="Commercial Real Estate" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2d52] via-[#0f2d52]/90 to-[#0f2d52]/70" />
        </div>
        
        {/* Triangle Pattern */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
          <svg viewBox="0 0 800 600" className="absolute w-full h-full">
            <polygon points="400,50 750,550 50,550" stroke="#48d480" strokeWidth="2" fill="none" />
            <polygon points="400,100 700,530 100,530" stroke="#48d480" strokeWidth="1.5" fill="none" />
          </svg>
        </div>

        <div className="container relative z-10 py-16">
          <div className="max-w-3xl">
            {/* Tag */}
            <div className="inline-flex items-center gap-2 bg-[#48d480]/20 border border-[#48d480]/40 rounded-full px-4 py-1.5 mb-6">
              <Building2 size={16} className="text-[#48d480]" />
              <span 
                style={{ fontFamily: "'Inter', sans-serif" }} 
                className="text-[#48d480] text-xs font-semibold tracking-wide"
              >
                Commercial Real Estate Financing
              </span>
            </div>

            {/* Headline */}
            <h1
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.1] mb-6"
            >
              Commercial RE
              <br />
              <span className="text-[#48d480]">Lending Solutions</span>
            </h1>

            {/* Subhead */}
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/70 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl"
            >
              From fix & flip to long-term rentals, new construction to multi-family — 
              access flexible financing options tailored for real estate investors at every level.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://app.trulli.ai/marketplace/legacytriangle"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-base bg-[#e8821c] hover:bg-[#d67518] text-white font-bold px-8 py-4 rounded-lg transition-colors shadow-lg"
              >
                Apply Now <ArrowRight size={18} />
              </a>
              <a
                href="#loan-programs"
                className="btn-outline-white flex items-center justify-center gap-2 text-base"
              >
                View Loan Programs
              </a>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/10">
              <div>
                <p className="text-3xl font-black text-[#48d480]">$75M+</p>
                <p className="text-white/60 text-sm">Max Loan Amount</p>
              </div>
              <div>
                <p className="text-3xl font-black text-[#48d480]">5.25%</p>
                <p className="text-white/60 text-sm">Rates Starting At</p>
              </div>
              <div>
                <p className="text-3xl font-black text-[#48d480]">100%</p>
                <p className="text-white/60 text-sm">Rehab Financing</p>
              </div>
              <div>
                <p className="text-3xl font-black text-[#48d480]">10 Days</p>
                <p className="text-white/60 text-sm">Fast Closings</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="w-12 h-12 rounded-lg bg-[#48d480]/10 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#48d480]" />
                  </div>
                  <h3 
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-lg font-bold text-[#0f2d52] mb-2"
                  >
                    {benefit.title}
                  </h3>
                  <p 
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-gray-600 text-sm"
                  >
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Loan Programs Section */}
      <section id="loan-programs" className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <span 
              className="inline-block bg-[#48d480]/10 text-[#48d480] text-xs font-bold px-4 py-1.5 rounded-full mb-4"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              FINANCING OPTIONS
            </span>
            <h2 
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-black text-[#0f2d52] mb-4"
            >
              Our Loan Programs
            </h2>
            <p 
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-gray-600 text-lg max-w-2xl mx-auto"
            >
              Click on any program below to view detailed loan criteria, leverage tiers, and rates.
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {loanPrograms.map((program) => (
              <LoanProgramCard
                key={program.id}
                program={program}
                isExpanded={expandedProgram === program.id}
                onToggle={() => toggleProgram(program.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Calculators Section */}
      <section id="calculators" className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <span 
              className="inline-flex items-center gap-2 bg-[#0f2d52]/10 text-[#0f2d52] text-xs font-bold px-4 py-1.5 rounded-full mb-4"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <Calculator size={14} />
              INVESTMENT TOOLS
            </span>
            <h2 
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-black text-[#0f2d52] mb-4"
            >
              Loan Calculators
            </h2>
            <p 
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-gray-600 text-lg max-w-2xl mx-auto"
            >
              Use our calculators to estimate your loan amount, cash flow, and investment returns.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            <FixFlipCalculator />
            <DSCRCalculator />
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-[#0f2d52]">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-black text-white mb-6"
            >
              Why Choose Legacy Triangle?
            </h2>
            <p 
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/70 text-lg mb-12"
            >
              Our dedicated team brings deep commercial real estate expertise to every deal, 
              ensuring fast, reliable closings and tailored solutions for your investment strategy.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#48d480]/20 flex items-center justify-center mx-auto mb-4">
                  <Clock size={32} className="text-[#48d480]" />
                </div>
                <h3 className="text-white font-bold mb-2">Fast, Reliable Closings</h3>
                <p className="text-white/60 text-sm">Close in as little as 10-14 days</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#48d480]/20 flex items-center justify-center mx-auto mb-4">
                  <Shield size={32} className="text-[#48d480]" />
                </div>
                <h3 className="text-white font-bold mb-2">Non-Recourse Options</h3>
                <p className="text-white/60 text-sm">Protect your personal assets</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#48d480]/20 flex items-center justify-center mx-auto mb-4">
                  <Users size={32} className="text-[#48d480]" />
                </div>
                <h3 className="text-white font-bold mb-2">Dedicated Expert Team</h3>
                <p className="text-white/60 text-sm">Extensive CRE experience</p>
              </div>
            </div>

            <a
              href="https://app.trulli.ai/marketplace/legacytriangle"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#e8821c] hover:bg-[#d67518] text-white font-bold px-8 py-4 rounded-lg transition-colors shadow-lg"
            >
              Start Your Application <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  className="text-2xl md:text-3xl font-black text-[#0f2d52] mb-4"
                >
                  Ready to Fund Your Next Project?
                </h2>
                <p 
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  className="text-gray-600 mb-6"
                >
                  Whether you're a new investor or managing a large portfolio, 
                  we have the financing solution to help you scale.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://app.trulli.ai/marketplace/legacytriangle"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#e8821c] hover:bg-[#d67518] text-white font-bold px-6 py-3 rounded-lg transition-colors"
                  >
                    Apply Now <ArrowRight size={16} />
                  </a>
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2 border-2 border-[#0f2d52] text-[#0f2d52] font-bold px-6 py-3 rounded-lg hover:bg-[#0f2d52] hover:text-white transition-colors"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="bg-[#0f2d52]/5 rounded-xl p-6">
                  <h3 className="font-bold text-[#0f2d52] mb-4">Quick Program Overview</h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-[#48d480]" />
                      <span>Fix & Flip: Up to 100% financing</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-[#48d480]" />
                      <span>Rentals: 30-year terms, 5.25% rates</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-[#48d480]" />
                      <span>Multi-Family: 5+ units eligible</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-[#48d480]" />
                      <span>Construction: Ground-up financing</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-[#48d480]" />
                      <span>Portfolios: $2M - $75M+</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
